import { useState, useEffect } from 'react'
import { AlertTriangle, RefreshCw, X } from 'lucide-react'
import api, { audiosocketBotsApi } from '../services/api'
import { useAuth } from '../context/AuthContext'

// Bot alert banner. Merges two independent checks:
//   1. /bot-health — heartbeat-based (bots v0.26+): OFFLINE (heartbeats stopped)
//      or STUCK (can't load its config, with the bot's own reason).
//   2. /dead-bots  — outcome-based: took >=5 calls in the last 20 min but
//      completed none. Catches bots that report "ready" yet still fail calls,
//      and older bots that don't send heartbeats.
// A bot flagged by both is listed once (heartbeat reason wins).
// Each bot gets a one-click Restart (restarts its bot service) to recover it.
const KIND_LABEL = { offline: 'Offline', stuck: 'Stuck', dead: 'No calls completing' }

export default function DeadBotsBanner() {
  const { isAdmin } = useAuth()
  const [bots, setBots] = useState([])
  const [dismissed, setDismissed] = useState('')
  const [busy, setBusy] = useState({})   // { [machine_id]: true } while restarting
  const [done, setDone] = useState({})   // { [machine_id]: 'ok' | 'fail' }

  const poll = async () => {
    // Each source is optional — one failing (e.g. an older dashboard without
    // /bot-health) must not hide the other's alerts.
    const [health, dead] = await Promise.all([
      api.get('/audiosocket-bots/bot-health').then(r => r.data?.bots || []).catch(() => null),
      audiosocketBotsApi.getDeadBots().then(r => r.data?.bots || []).catch(() => null)
    ])
    if (health === null && dead === null) return   // keep last known state on transient errors
    const merged = []
    const seen = new Set()
    for (const b of health || []) {
      seen.add(b.machine_id)
      merged.push({ ...b, kind: b.state, detail: b.reason })
    }
    for (const b of dead || []) {
      if (b.machine_id && seen.has(b.machine_id)) continue
      merged.push({ ...b, kind: 'dead', detail: `${b.total_calls} calls in 20 min, none completed` })
    }
    setBots(merged)
  }

  useEffect(() => {
    if (!isAdmin) return
    let stop = false
    const run = async () => { if (!stop) await poll() }
    run()
    // 30s (was 60s): with /dead-bots' 90s minimum a dead bot shows up ~2-2.5 min
    // after it breaks, instead of burning calls for ~4-5 min first.
    const id = setInterval(run, 30000)
    return () => { stop = true; clearInterval(id) }
  }, [isAdmin])

  const runAction = async (b, fn, recheckMs) => {
    if (!b.machine_id) return
    setBusy(s => ({ ...s, [b.machine_id]: true }))
    setDone(d => { const n = { ...d }; delete n[b.machine_id]; return n })
    try {
      const { data } = await fn(b.machine_id)
      setDone(d => ({ ...d, [b.machine_id]: data?.success === false ? 'fail' : 'ok' }))
      setTimeout(poll, recheckMs)
    } catch (e) {
      setDone(d => ({ ...d, [b.machine_id]: 'fail' }))
    } finally {
      setBusy(s => { const n = { ...s }; delete n[b.machine_id]; return n })
    }
  }

  const restart = (b) => {
    if (!b.machine_id) return
    if (!window.confirm(`Restart bot ${b.sip_user} (${b.template_name || 'tpl ' + b.template_id}, lxc ${b.lxc_id})?\n\nThis restarts the bot service to recover it.`)) return
    runAction(b, audiosocketBotsApi.restartMachine, 6000)
  }

  const reboot = (b) => {
    if (!b.machine_id) return
    if (!window.confirm(`REBOOT the container for bot ${b.sip_user} (lxc ${b.lxc_id})?\n\nUse this if Restart failed — it hard-reboots the bot's whole container (recovers a frozen/wedged bot). Takes ~30s.`)) return
    runAction(b, audiosocketBotsApi.rebootContainer, 10000)
  }

  if (!isAdmin || bots.length === 0) return null
  const key = bots.map(b => `${b.sip_user}:${b.template_id}:${b.kind}`).sort().join(',')
  if (dismissed === key) return null

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, margin: '0 0 14px',
      padding: '12px 14px', borderRadius: 10,
      background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5'
    }}>
      <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: '#ef4444' }}>
          {bots.length} bot{bots.length === 1 ? ' needs' : 's need'} attention
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {bots.map((b) => {
            const mid = b.machine_id
            return (
              <div key={`${b.sip_user}-${b.template_id}-${b.kind}`} style={{
                display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
                background: 'rgba(0,0,0,0.15)', borderRadius: 8, padding: '5px 10px', fontSize: 12.5
              }}>
                <strong style={{ fontFamily: 'monospace' }}>{b.sip_user}</strong>
                <span style={{
                  fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3,
                  padding: '1px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.25)', color: '#fecaca'
                }}>{KIND_LABEL[b.kind] || b.kind}</span>
                <span style={{ opacity: 0.7, whiteSpace: 'normal' }}>
                  {b.template_name || `tpl ${b.template_id}`}{b.lxc_id ? ` · lxc ${b.lxc_id}` : ''}{b.server_name ? ` · ${b.server_name}` : ''} · {b.detail}
                </span>
                {done[mid] === 'ok' ? (
                  <span style={{ color: '#22c55e', fontWeight: 600 }}>✓ restarted</span>
                ) : done[mid] === 'fail' ? (
                  <span style={{ color: '#ef4444', fontWeight: 600 }}>✗ failed</span>
                ) : null}
                {mid ? (
                  <>
                    <button
                      onClick={() => restart(b)}
                      disabled={!!busy[mid]}
                      title={`Restart bot ${b.sip_user} service`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5, cursor: busy[mid] ? 'wait' : 'pointer',
                        background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6,
                        padding: '4px 10px', fontSize: 12, fontWeight: 600
                      }}>
                      <RefreshCw size={12} className={busy[mid] ? 'spin' : ''} />
                      {busy[mid] ? 'Working…' : 'Restart'}
                    </button>
                    <button
                      onClick={() => reboot(b)}
                      disabled={!!busy[mid]}
                      title={`Hard-reboot bot ${b.sip_user}'s container (use if Restart fails / bot is wedged)`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5, cursor: busy[mid] ? 'wait' : 'pointer',
                        background: 'transparent', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.6)', borderRadius: 6,
                        padding: '4px 10px', fontSize: 12, fontWeight: 600
                      }}>
                      Reboot
                    </button>
                  </>
                ) : (
                  // Calls arrive for this SIP but the dashboard has no bot for it
                  // (deleted, or re-cloned under another template) — nothing to restart.
                  <span
                    title="The dialler still sends calls to this SIP, but there is no bot for it in the dashboard. Remove the SIP from the dialler's campaign/carrier if it was retired, or clone it again from its template."
                    style={{
                      whiteSpace: 'normal', fontSize: 11.5, fontWeight: 600, color: '#fde68a',
                      background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)',
                      borderRadius: 6, padding: '3px 8px', cursor: 'help'
                    }}>
                    No bot for this SIP — remove it from the dialler, or clone it again
                  </span>
                )}
              </div>
            )
          })}
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>
          Offline = no heartbeat for 90s · Stuck = can't load its config for 2+ min · No calls completing = ≥5 calls (failing for 90s+), none finished.
          Click Restart to recover the bot; if it persists, it may need a container reboot (or, for Stuck, check the reason — often the dashboard/DB).
        </div>
      </div>
      <button onClick={() => setDismissed(key)} title="Dismiss until the set changes"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#fca5a5', flexShrink: 0 }}>
        <X size={16} />
      </button>
    </div>
  )
}
